import { gql, useLazyQuery } from '@apollo/client';
import { ActionModal, Button } from '@wistia/vhs';
import { trackStructEvent } from '~/utilities/snowplow';
import { isNonEmptyString, isNotNil } from '~/utilities/type-guards';
import { DownloadsTable } from './DownloadsTable';
import { noOpFn } from '~/utilities/util';
import { useMemo } from 'react';
import { useLoadFeatureFlags } from '../hooks/useLoadFeatureFlags';
import { DownloadModal } from './DownloadModal/DownloadModal';

// #region graphql
const mediaFileFragment = gql`
  fragment DownloadableMediaFilesModal_MediaFile on MediaFile {
    id
    displayName
    extension
    width
    height
    length
    formattedFilesize
    downloadUrl
    captionedDownloadUrl
    createdAt
  }
`;

const MODAL_QUERY_WITH_EDIT_EXPORTS = gql`
  query EDIT_EXPORT_FILES_MODAL_QUERY($mediaHashedId: HashedId!) {
    media(hashedId: $mediaHashedId) {
      id
      hasIsos
      __typename
      downloadableMediaFiles {
        nodes {
          id
          ...DownloadableMediaFilesModal_MediaFile
        }
      }
      editExport {
        id
        originalFile {
          id
          ...DownloadableMediaFilesModal_MediaFile
        }
      }
      captionableTranscripts {
        id
        hashedId
        englishName
        activeVersion
        ietfLanguageTag
      }
    }
  }
  ${mediaFileFragment}
`;

const ANONYMOUS_MODAL_QUERY = gql`
  query ANONYMOUS_DOWNLOAD_MEDIA_FILES_MODAL_QUERY($mediaHashedId: HashedId!) {
    media: anonymousMedia(hashedId: $mediaHashedId) {
      id
      __typename
      downloadableMediaFiles {
        nodes {
          id
          ...DownloadableMediaFilesModal_MediaFile
        }
      }
    }
  }
  ${mediaFileFragment}
`;
// #endregion

const dateStringToTimestamp = (dateString) => new Date(dateString).getTime();

const dedupeAndRename = (files) => {
  return (
    files
      .map((file) =>
        file.displayName === 'Original File' ? { ...file, displayName: 'Uncompressed' } : file,
      )
      .sort((a, b) => dateStringToTimestamp(b.createdAt) - dateStringToTimestamp(a.createdAt))
      // TODO: When Media Platform has a fix for the bug where derivatives are being duplicated, we should remove this filter operation.
      .filter(
        (file, index, array) =>
          array.findIndex((f) => f.displayName === file.displayName) === index,
      )
  );
};

const renameExportedFile = (editExport) => {
  if (isNotNil(editExport?.originalFile)) {
    return {
      ...editExport.originalFile,
      displayName: 'Source (unedited)',
    };
  }
  return null;
};

export const DownloadMediaFilesModal = ({
  mediaHashedId,
  toggle,
  userIsAnonymousViewer = false,
  trackingLabel = '',
  onDownloadClick = noOpFn,
  onClose = () => {},
}) => {
  const { flags } = useLoadFeatureFlags(['flag_improved_download_modal']);
  const improvedDownloadModalEnabled = flags.flag_improved_download_modal.enabled;

  const GQL_QUERY = useMemo(() => {
    if (userIsAnonymousViewer) {
      return ANONYMOUS_MODAL_QUERY;
    }

    return MODAL_QUERY_WITH_EDIT_EXPORTS;
  }, [userIsAnonymousViewer]);

  const [loadQuery, { data }] = useLazyQuery(GQL_QUERY, {
    variables: { mediaHashedId },
    fetchPolicy: 'no-cache',
  });

  const {
    media: { __typename, downloadableMediaFiles, editExport, captionableTranscripts },
  } = data || { media: {} };

  return improvedDownloadModalEnabled ? (
    <DownloadModal
      mediaHashedId={mediaHashedId}
      isAnonymousViewer={userIsAnonymousViewer}
      toggle={
        toggle ??
        ((openModal) => (
          <Button
            onClick={openModal}
            data-testid="download_button"
          >
            Download
          </Button>
        ))
      }
      onClose={onClose}
    />
  ) : (
    <ActionModal
      toggle={(show, ref) => {
        const triggerLoad = () => {
          loadQuery();
          show();
          if (isNonEmptyString(trackingLabel)) {
            trackStructEvent({
              category: 'link',
              action: 'click',
              label: trackingLabel,
            });
          }
        };

        return toggle ? (
          toggle(triggerLoad)
        ) : (
          <Button
            data-testid="download_button"
            ref={ref}
            onClick={triggerLoad}
          >
            Download
          </Button>
        );
      }}
      title="Download"
      onHide={() => {
        onClose?.();
      }}
      key="download_modal"
      data-testid="download_modal"
      render={(hide) => {
        return (
          data && (
            <DownloadsTable
              downloadableMediaFiles={dedupeAndRename(downloadableMediaFiles.nodes)}
              replacedMediaFile={renameExportedFile(editExport)}
              mediaType={__typename}
              mediaHashedId={mediaHashedId}
              onDownloadClick={onDownloadClick}
              transcripts={captionableTranscripts ?? []}
              hasIsos={data?.media?.hasIsos ?? false}
              mediaId={data.media?.id}
              hideModal={hide}
            />
          )
        );
      }}
    />
  );
};
